export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (h < 0 || h > 1 || s < 0 || s > 1 || l < 0 || l > 1) {
    throw new Error('H, S, L are expected to be in [0..1] range');
  }
  let r = 0,
    g = 0,
    b = 0;
  if (s == 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const _hue2rgb = (t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    r = _hue2rgb(h + 1 / 3);
    g = _hue2rgb(h);
    b = _hue2rgb(h - 1 / 3);
  }
  return [r, g, b];
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  if (r < 0 || r > 1 || g < 0 || g > 1 || b < 0 || b > 1) {
    throw new Error('R, G, B are expected to be in [0..1] range');
  }
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0,
    s = 0;
  if (max == min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h, s, l];
}

export function rgbToHex(r: number, g: number, b: number): number {
  if (r < 0 || r > 1 || g < 0 || g > 1 || b < 0 || b > 1) {
    throw new Error('R, G, B are expected to be in [0..1] range');
  }
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);
}

export function hexToRgb(n: number): [number, number, number] {
  if (n < 0 || n > 0xffffff) {
    throw new Error('N is expected to be in [0..0xffffff] range');
  }
  return [((n >> 16) & 0x0ff) / 255, ((n >> 16) & 0x0ff) / 255, (n & 0x0ff) / 255];
}

export function hslToHex(h: number, s: number, l: number): number {
  return rgbToHex(...hslToRgb(h, s, l));
}

export function hexToHsl(n: number): [number, number, number] {
  return rgbToHsl(...hexToRgb(n));
}

export function hslToCssStr(h: number, s: number, l: number): string {
  if (h < 0 || h > 1 || s < 0 || s > 1 || l < 0 || l > 1) {
    throw new Error('H, S, L are expected to be in [0..1] range');
  }
  return 'hsl(' + 360 * h + ',' + 100 * s + '%,' + 100 * l + '%)';
}

export function cssColorToHexNumber(cssColor: string): number | null {
  cssColor = cssColor.trim().toLowerCase();

  if (cssColor.startsWith('rgb')) {
    const match = cssColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(,\s*\d+)?\)/);
    if (match) {
      const [, red, green, blue] = match.slice(1, 4);
      const hexValue = Number(rgbToHexString([Number(red), Number(green), Number(blue)], '0x'));
      return hexValue;
    }
  }

  if (cssColor.startsWith('#')) {
    const hexValue =
      cssColor.length === 4
        ? `0x${cssColor[1]}${cssColor[1]}${cssColor[2]}${cssColor[2]}${cssColor[3]}${cssColor[3]}`
        : `0x${cssColor.substr(1)}`;
    return Number(hexValue);
  }

  return null;
}

export function hueColorMapper(valueRange: [number, number], hueRangeDeg: [number, number]): (value: number) => number {
  if (valueRange[0] >= valueRange[1]) {
    throw new Error('Invalid values range');
  }
  if (hueRangeDeg[0] == hueRangeDeg[1]) {
    throw new Error('Invalid output range');
  }
  if (hueRangeDeg[0] < 0 || hueRangeDeg[0] > 360 || hueRangeDeg[1] < 0 || hueRangeDeg[1] > 360) {
    throw new Error('Hue is expected to be in [0..360] range - degrees.');
  }
  const valueRangeWidth = valueRange[1] - valueRange[0];
  const hueRangeDegWidth = hueRangeDeg[1] - hueRangeDeg[0];
  return (value: number): number => {
    if (value < valueRange[0]) return hueRangeDeg[0];
    if (value > valueRange[1]) return hueRangeDeg[1];
    const factor = (value - valueRange[0]) / valueRangeWidth;
    const resHueDeg = hueRangeDeg[0] + factor * hueRangeDegWidth;
    return resHueDeg;
  };
}

export function colorMapper<T>(
  hueConverter: (h: number, s: number, l: number) => T,
  valueRange: [number, number],
  hueRangeDeg: [number, number],
  s = 1.0,
  l = 0.5
): (value: number) => T {
  if (s < 0 || s > 1 || l < 0 || l > 1) {
    throw new Error('S, L are expected to be in [0..1] range');
  }
  const delegate = hueColorMapper(valueRange, hueRangeDeg);
  return (value: number): T => {
    return hueConverter(delegate(value) / 360, s, l);
  };
}

export function rgbColorMapper(
  valueRange: [number, number],
  hueRangeDeg: [number, number],
  s = 1.0,
  l = 0.5
): (value: number) => [number, number, number] {
  return colorMapper(hslToRgb, valueRange, hueRangeDeg, s, l);
}

export function cssColorMapper(
  valueRange: [number, number],
  hueRangeDeg: [number, number],
  s = 1.0,
  l = 0.5
): (value: number) => string {
  return colorMapper(hslToCssStr, valueRange, hueRangeDeg, s, l);
}

export function hexColorMapper(
  valueRange: [number, number],
  hueRangeDeg: [number, number],
  s = 1.0,
  l = 0.5
): (value: number) => number {
  return colorMapper(hslToHex, valueRange, hueRangeDeg, s, l);
}

export function shadeColor(color: string, percent: number): string {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = (R * (100 + percent)) / 100;
  G = (G * (100 + percent)) / 100;
  B = (B * (100 + percent)) / 100;

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  R = Math.round(R);
  G = Math.round(G);
  B = Math.round(B);

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
}

export function decimalToRgb(c: number): [number, number, number] {
  if (c < 0 || c > 0xffffff) {
    throw new Error('Invalid Decimal RGB color');
  }
  return [(c & 0xff0000) >> 16, (c & 0x00ff00) >> 8, c & 0x0000ff];
}

export function rgbToDecimal(rgb: ArrayLike<number>): number {
  if (rgb.length != 3 || rgb[0] < 0 || rgb[0] > 0xff || rgb[1] < 0 || rgb[1] > 0xff || rgb[2] < 0 || rgb[1] > 0xff) {
    throw new Error('Invalid RGB color components');
  }
  return (rgb[0] << 16) + (rgb[1] << 8) + rgb[2];
}

export function rgbToHexString(rgb: ArrayLike<number>, hexPrefix = '#'): string {
  if (rgb.length != 3 || rgb[0] < 0 || rgb[0] > 0xff || rgb[1] < 0 || rgb[1] > 0xff || rgb[2] < 0 || rgb[1] > 0xff) {
    throw new Error('Invalid RGB color components');
  }
  return `${hexPrefix}${rgb[0].toString(16).padStart(2, '0')}${rgb[1].toString(16).padStart(2, '0')}${rgb[2].toString(16).padStart(2, '0')}`;
}

export function randomColorRGB(): number {
  return rgbToDecimal(crypto.getRandomValues(new Uint8Array(3)));
}

export function LightenDarkenColor(col: string, amt: number): string {
  let usePound = false;

  if (col[0] == '#') {
    col = col.slice(1);
    usePound = true;
  }

  const num = parseInt(col, 16);

  let r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000ff) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
}

export function getQualitative6Pale(): Array<string> {
  // https://personal.sron.nl/~pault/#sec:qualitative
  return ['#BBCCEE', '#CCEEFF', '#CCDDAA', '#EEEEBB', '#FFCCCC', '#DDDDDD'];
}

export function getQualitative6Dark(): Array<string> {
  // https://personal.sron.nl/~pault/#sec:qualitative
  return ['#222255', '#225555', '#225522', '#666633', '#663333', '#555555'];
}

export function getQualitative7Bright(): Array<string> {
  // https://personal.sron.nl/~pault/#sec:qualitative
  return ['#4477AA', '#EE6677', '#228833', '#CCBB44', '#66CCEE', '#AA3377', '#BBBBBB'];
}

export function getQualitative7Vibrant(): Array<string> {
  // https://personal.sron.nl/~pault/#sec:qualitative
  return ['#EE7733', '#0077BB', '#33BBEE', '#EE3377', '#CC3311', '#009988', '#BBBBBB'];
}

export function getQualitative9Muted(): Array<string> {
  // https://personal.sron.nl/~pault/#sec:qualitative
  return ['#CC6677', '#332288', '#DDCC77', '#117733', '#88CCEE', '#882255', '#44AA99', '#999933', '#AA4499'];
}

export function getQualitative9Light(): Array<string> {
  // https://personal.sron.nl/~pault/#sec:qualitative
  return ['#77AADD', '#EE8866', '#EEDD88', '#FFAABB', '#99DDFF', '#44BB99', '#BBCC33', '#AAAA00', '#DDDDDD'];
}

export function getColorblind12Main(): Array<string> {
  //https://mk.bcgsc.ca/colorblind/palettes.mhtml#12-color-palette-for-colorbliness
  return [
    '#008DF9',
    '#FF5AAF',
    '#FFC33B',
    '#009F81',
    '#00C2F9',
    '#9F0162',
    '#FFB2FD',
    '#A40122',
    '#E20134',
    '#FF6E3A',
    '#8400CD',
    '#00FCCF'
  ];
}

export function getColorblind12Alt(): Array<string> {
  //https://mk.bcgsc.ca/colorblind/palettes.mhtml#12-color-palette-for-colorbliness
  return [
    '#006A5E',
    '#ED0D88',
    '#00BDA9',
    '#FFC4D4',
    '#0058CC',
    '#D208FB',
    '#FF66FD',
    '#00EFF9',
    '#156D03',
    '#009719',
    '#00C61B',
    '#00FB1D'
  ];
}

export function getColorblind15Main(): Array<string> {
  //https://mk.bcgsc.ca/colorblind/palettes.mhtml#15-color-palette-for-colorbliness
  return [
    '#68023F',
    '#008169',
    '#EF0096',
    '#00DCB5',
    '#FFCFE2',
    '#003C86',
    '#9400E6',
    '#009FFA',
    '#FF71FD',
    '#7CFFFA',
    '#6A0213',
    '#008607',
    '#F60239',
    '#00E307',
    '#FFDC3D'
  ];
}

export function getColorblind15Alt(): Array<string> {
  //https://mk.bcgsc.ca/colorblind/palettes.mhtml#15-color-palette-for-colorbliness
  return [
    '#00463C',
    '#C00B6F',
    '#00A090',
    '#FF95BA',
    '#5FFFDE',
    '#590A87',
    '#0063E5',
    '#ED0DFD',
    '#00C7F9',
    '#FFD5FD',
    '#3D3C04',
    '#C80B2A',
    '#00A51C',
    '#FFA035',
    '#9BFF2D'
  ];
}

export function getColorblind24Main(): Array<string> {
  //https://mk.bcgsc.ca/colorblind/palettes.mhtml#24-color-palette-for-colorbliness
  return [
    '#003D30',
    '#005745',
    '#00735C',
    '#009175',
    '#00AF8E',
    '#00CBA7',
    '#00EBC1',
    '#86FFDE',
    '#00306F',
    '#00489E',
    '#005FCC',
    '#0079FA',
    '#009FFA',
    '#00C2F9',
    '#00E5F8',
    '#7CFFFA',
    '#004002',
    '#005A01',
    '#007702',
    '#009503',
    '#00B408',
    '#00D302',
    '#00F407',
    '#AFFF2A'
  ];
}

export function getColorblind24Alt(): Array<string> {
  //https://mk.bcgsc.ca/colorblind/palettes.mhtml#24-color-palette-for-colorbliness
  return [
    '#5A0A33',
    '#810D49',
    '#AB0D61',
    '#D80D7B',
    '#FF2E95',
    '#FF78AD',
    '#FFACC6',
    '#FFD7E1',
    '#460B70',
    '#6B069F',
    '#8E06CD',
    '#B40AFC',
    '#ED0DFD',
    '#FF66FD',
    '#FFA3FC',
    '#FFD5FD',
    '#5F0914',
    '#86081C',
    '#B20725',
    '#DE0D2E',
    '#FF4235',
    '#FF8735',
    '#FFB935',
    '#FFE239'
  ];
}

export function getDistinctColors18(): Array<string> {
  // https://sashamaps.net/docs/resources/20-colors/
  return [
    '#e6194b',
    '#3cb44b',
    '#ffe119',
    '#4363d8',
    '#f58231',
    '#911eb4',
    '#46f0f0',
    '#f032e6',
    '#bcf60c',
    '#fabebe',
    '#008080',
    '#e6beff',
    '#9a6324',
    '#fffac8',
    '#800000',
    '#aaffc3',
    '#808000',
    '#ffd8b1',
    '#000075',
    '#808080'
  ];
}

export function getDistinctColors26(): Array<string> {
  // https://en.wikipedia.org/wiki/Help:Distinguishable_colors
  return [
    '#F0A3FF', // Amethyst
    '#0075DC', // Blue
    '#993F00', // Caramel
    '#4C005C', // Damson
    '#191919', // Ebony
    '#005C31', // Forest
    '#2BCE48', // Green
    '#FFCC99', // Honeydew
    '#808080', // Iron
    '#94FFB5', // Jade
    '#8F7C00', // Khaki
    '#9DCC00', // Lime
    '#C20088', // Mallow
    '#003380', // Navy
    '#FFA405', // Orpiment
    '#FFA8BB', // Pink
    '#426600', // Quagmire
    '#FF0010', // Red
    '#5EF1F2', // Sky
    '#00998F', // Turquoise
    '#E0FF66', // Uranium
    '#740AFF', // Violet
    '#990000', // Wine
    '#FFFF80', //	Xanthin
    '#FFE100', // Yellow
    '#FF5005' // Zinnia
  ];
}

export function getDistinctColors53(): Array<string> {
  // https://gist.github.com/mattkrick/0f3ad2ff648a4eada65c
  return [
    '#F44336',
    '#673AB7',
    '#03A9F4',
    '#4CAF50',
    '#FFEB3B',
    '#FF5722',
    '#607D8B',
    '#E91E63',
    '#3F51B5',
    '#00BCD4',
    '#8BC34A',
    '#FFC107',
    '#795548',
    '#9C27B0',
    '#2196F3',
    '#009688',
    '#CDDC39',
    '#FF9800',
    '#9E9E9E',
    '#EF9A9A',
    '#B39DDB',
    '#81D4FA',
    '#A5D6A7',
    '#FFF59D',
    '#FFAB91',
    '#B0BEC5',
    '#F48FB1',
    '#9FA8DA',
    '#80DEEA',
    '#C5E1A5',
    '#FFE082',
    '#BCAAA4',
    '#CE93D8',
    '#90CAF9',
    '#80CBC4',
    '#E6EE9C',
    '#FFCC80',
    '#EEEEEE',
    '#B71C1C',
    '#311B92',
    '#01579B',
    '#1B5E20',
    '#F57F17',
    '#BF360C',
    '#263238',
    '#880E4F',
    '#1A237E',
    '#006064',
    '#33691E',
    '#FF6F00',
    '#3E2723',
    '#4A148C',
    '#0D47A1',
    '#004D40',
    '#827717',
    '#E65100',
    '#212121'
  ];
}

export class DistinctColorsGenerator {
  //https://martin.ankerl.com/2009/12/09/how-to-create-random-colors-programmatically/
  private readonly _step = 0.6180339887498948; // 1/phi (phi - golden_ratio)

  private _hue: number;

  private _saturation: number;

  private _lightness: number;

  public constructor(initialHue = 0, initialSaturation = 0, initialLightness = 0.6) {
    this._hue = initialHue;
    this._saturation = initialSaturation;
    this._lightness = initialLightness;
  }

  private _next(): void {
    this._hue += this._step;
    this._hue %= 1;
    this._saturation += this._step;
    this._saturation %= 1;
    this._lightness += this._step;
    this._lightness %= 1;
  }

  public getHSL(): [number, number, number] {
    const ret: [number, number, number] = [this._hue, this._saturation / 2 + 0.5, this._lightness / 3 + 0.3];
    this._next();
    return ret;
  }
}
